import type { ReactNode } from 'react';
import type { LegendaLang } from './vocabulary';

/** What the system actually looked at before reporting absence. */
export interface SearchReceipt {
  sourceGroups: number;
  pages: number;
  evidenceRegions: number;
  /** Gap record id, e.g. `GAP-1982-03`. */
  gapRecordId?: string;
  blocksPublication?: boolean;
  /** What would resolve it — a document to find, a survey to run. */
  resolvedBy?: string;
}

export interface GapNoticeProps {
  title: string;
  lang: LegendaLang;
  explanation: string;
  receipt: SearchReceipt;
  actions?: ReactNode;
}

const COPY = {
  ru: {
    searched: 'просмотрено',
    groups: 'групп источников',
    pages: 'страниц',
    regions: 'регионов доказательств',
    record: 'запись пробела',
    blocks: 'блокирует публикацию',
    yes: 'да',
    no: 'нет',
    resolvedBy: 'что снимет пробел',
  },
  hy: {
    searched: 'դիտարկվել է',
    groups: 'աղբյուրների խումբ',
    pages: 'էջ',
    regions: 'ապացույցի տիրույթ',
    record: 'բացի գրառում',
    blocks: 'արգելափակում է հրապարակումը',
    yes: 'այո',
    no: 'ոչ',
    resolvedBy: 'ինչը կլրացնի բացը',
  },
  en: {
    searched: 'searched',
    groups: 'source groups',
    pages: 'pages',
    regions: 'evidence regions',
    record: 'gap record',
    blocks: 'blocks publication',
    yes: 'yes',
    no: 'no',
    resolvedBy: 'resolved by',
  },
} as const satisfies Record<LegendaLang, Record<string, string>>;

/**
 * «Нет данных» — a success state, rendered as a negative result.
 *
 * The brief is explicit that a design making emptiness feel like failure will
 * push users toward the one behaviour this product exists to prevent. So this
 * component never borrows the error state's colour and never apologises. It is
 * dashed and hatched in neutral ink, and it *shows its work*: what was
 * searched, which gap record holds the absence, whether it blocks publication,
 * and what would change the answer.
 *
 * An empty state that can name the missing document reads as rigour. One that
 * just says "no results" reads as an outage.
 */
export default function GapNotice({
  title,
  lang,
  explanation,
  receipt,
  actions,
}: GapNoticeProps) {
  const copy = COPY[lang];
  const n = (value: number) => value.toLocaleString(lang === 'en' ? 'en-GB' : 'ru-RU');

  return (
    <section className="lg-gap lg-hatch-void" data-state="no-data">
      <p className="lg-gap__title">{title}</p>
      <p className="lg-gap__body">{explanation}</p>

      <div className="lg-gap__receipt">
        {copy.searched}: {n(receipt.sourceGroups)} {copy.groups} · {n(receipt.pages)} {copy.pages} ·{' '}
        {n(receipt.evidenceRegions)} {copy.regions}
        {receipt.gapRecordId ? (
          <>
            <br />
            {copy.record}: {receipt.gapRecordId}
            {receipt.blocksPublication !== undefined
              ? ` · ${copy.blocks}: ${receipt.blocksPublication ? copy.yes : copy.no}`
              : null}
          </>
        ) : null}
        {receipt.resolvedBy ? (
          <>
            <br />
            {copy.resolvedBy}: {receipt.resolvedBy}
          </>
        ) : null}
      </div>

      {actions ? <div className="lg-ev__meta">{actions}</div> : null}
    </section>
  );
}
